/**
 * Enhanced Thinking Animation
 * Natural "thinking" pose: look down and away, pronounced head tilt
 * Mimics real contemplative behavior (not looking up which is "remembering")
 * Nuamedia Solutions Pty Ltd
 */
import * as THREE from 'three';
import React from 'react';
import { animationController } from './UnifiedAnimationController';

export interface ThinkingAnimationRefs {
  leftEyeRef?: React.RefObject<THREE.Object3D>;
  rightEyeRef?: React.RefObject<THREE.Object3D>;
  headRef?: React.RefObject<THREE.Object3D>;
}

/** Optional config from the avatar editor's Animation panel */
export interface ThinkingAnimationConfig {
  eyeSearchIntensity?: number;  // 0-1, how much eyes wander (default 0.5)
  suppressHeadMovement?: boolean;
}

export const setupThinkingAnimation = (
  leftEyeRef?: React.RefObject<THREE.Object3D>,
  rightEyeRef?: React.RefObject<THREE.Object3D>,
  headRef?: React.RefObject<THREE.Object3D>,
  config?: ThinkingAnimationConfig
) => {
  if (!headRef?.current && (!leftEyeRef?.current || !rightEyeRef?.current)) {
    console.warn('No usable refs available for thinking animation');
    return null;
  }

  let animationFrameId: number | null = null;
  let resetAnimationFrameId: number | null = null; // Track reset animation to prevent orphaned loops
  let transitionAnimationFrameId: number | null = null; // Track active pose transitions so speaking can cancel them
  let startTime = Date.now();
  let isCancelled = false; // Flag for immediate cancellation

  // Store initial positions
  const initialLeftEyeRotation = {
    x: leftEyeRef?.current?.rotation.x || 0,
    y: leftEyeRef?.current?.rotation.y || 0
  };
  const initialRightEyeRotation = {
    x: rightEyeRef?.current?.rotation.x || 0,
    y: rightEyeRef?.current?.rotation.y || 0
  };
  const initialHeadRotation = headRef?.current ? {
    x: headRef.current.rotation.x || 0,
    y: headRef.current.rotation.y || 0,
    z: headRef.current.rotation.z || 0
  } : null;
  const hasExistingHeadPitch = Boolean(initialHeadRotation && Math.abs(initialHeadRotation.x) > 0.035);
  const hasExistingHeadTurn = Boolean(initialHeadRotation && Math.abs(initialHeadRotation.y) > 0.05);
  const hasExistingHeadTilt = Boolean(initialHeadRotation && Math.abs(initialHeadRotation.z) > 0.04);

  const cancelTrackedFrames = () => {
    if (animationFrameId) {
      animationController.untrackRafId(animationFrameId);
      cancelAnimationFrame(animationFrameId);
      animationFrameId = null;
    }

    if (resetAnimationFrameId) {
      animationController.untrackRafId(resetAnimationFrameId);
      cancelAnimationFrame(resetAnimationFrameId);
      resetAnimationFrameId = null;
    }

    if (transitionAnimationFrameId) {
      animationController.untrackRafId(transitionAnimationFrameId);
      cancelAnimationFrame(transitionAnimationFrameId);
      transitionAnimationFrameId = null;
    }
  };

  const animate = () => {
    // Check if cancelled (immediate speaking takeover)
    if (isCancelled || animationController.isSpeaking()) {
      console.log('🤔 Thinking animation cancelled - speaking active');
      return;
    }

    const now = Date.now();
    const elapsed = (now - startTime) / 1000;
    const progress = Math.min(elapsed / 0.8, 1); // Slower transition for more thoughtful appearance

    // THINKING POSE: Subtle contemplative look (refined for natural appearance)
    // Looking down/away = thinking/processing (not up = remembering)
    // eyeSearchIntensity: 0-1, scales look/wander amounts
    const intensity = config?.eyeSearchIntensity ?? 0.5;
    const lookDownAmount = -0.03 - (intensity * 0.04);   // Gentle look down
    const lookAwayAmount = 0.04 + (intensity * 0.06);    // Gentle look away to side

    // Eye wandering after pose is established (like eyes searching for answer)
    const wanderScale = 0.001 + (intensity * 0.003);
    const wandering = progress >= 1 ? Math.sin(elapsed * 0.2) * wanderScale : 0;

    if (leftEyeRef?.current && rightEyeRef?.current) {
      // Apply smooth easing
      const easedProgress = easeOutQuad(progress);

      // Base thinking look direction (down and away)
      const baseX = initialLeftEyeRotation.x + (lookDownAmount * easedProgress);
      const baseY = initialLeftEyeRotation.y + (lookAwayAmount * easedProgress);

      // Apply with subtle wandering (eyes never perfectly still when thinking)
      leftEyeRef.current.rotation.x = baseX + wandering * 0.8;
      leftEyeRef.current.rotation.y = baseY + wandering;

      // Right eye with natural asymmetry
      rightEyeRef.current.rotation.x = baseX + wandering * 0.75;
      rightEyeRef.current.rotation.y = baseY + wandering * 0.9;
    }

    // Head tilt for subtle "thinking" pose (refined for natural appearance)
    if (headRef?.current && initialHeadRotation) {
      const suppressHeadMovement = config?.suppressHeadMovement === true;
      // Respect an existing pose so thinking does not keep stacking more tilt.
      const headTiltAmount = suppressHeadMovement || hasExistingHeadTilt ? 0 : 0.05;       // Subtle tilt (REDUCED for natural look)
      const headLookDownAmount = suppressHeadMovement || hasExistingHeadPitch ? 0 : 0.02;  // Gentle downward tilt (REDUCED)
      const headTurnAmount = suppressHeadMovement || hasExistingHeadTurn ? 0 : 0.03;       // Gentle turn away (REDUCED)

      // Use lerp for smooth continuous movement
      headRef.current.rotation.z = THREE.MathUtils.lerp(
        headRef.current.rotation.z || 0,
        initialHeadRotation.z + headTiltAmount * easeOutQuad(progress),
        0.06
      );
      headRef.current.rotation.x = THREE.MathUtils.lerp(
        headRef.current.rotation.x || 0,
        initialHeadRotation.x + headLookDownAmount * easeOutQuad(progress),
        0.06
      );
      headRef.current.rotation.y = THREE.MathUtils.lerp(
        headRef.current.rotation.y || 0,
        initialHeadRotation.y + headTurnAmount * easeOutQuad(progress),
        0.06
      );
    }

    animationFrameId = requestAnimationFrame(animate);
    // Track RAF ID with controller for immediate cancellation
    if (animationFrameId) {
      animationController.trackRafId(animationFrameId);
    }
  };

  // Easing function for smoother motion
  const easeOutQuad = (t: number): number => t * (2 - t);

  const cleanup = () => {
    cancelTrackedFrames();

    // Smoothly return to original position
    const resetAnimation = () => {
      const eyesStillMoving =
        Boolean(leftEyeRef?.current && rightEyeRef?.current) && (
          Math.abs(leftEyeRef!.current!.rotation.x - initialLeftEyeRotation.x) > 0.001 ||
          Math.abs(leftEyeRef!.current!.rotation.y - initialLeftEyeRotation.y) > 0.001 ||
          Math.abs(rightEyeRef!.current!.rotation.x - initialRightEyeRotation.x) > 0.001 ||
          Math.abs(rightEyeRef!.current!.rotation.y - initialRightEyeRotation.y) > 0.001
        );

      const headStillMoving = headRef?.current && initialHeadRotation ? (
        Math.abs(headRef.current.rotation.x - initialHeadRotation.x) > 0.001 ||
        Math.abs(headRef.current.rotation.y - initialHeadRotation.y) > 0.001 ||
        Math.abs(headRef.current.rotation.z - initialHeadRotation.z) > 0.001
      ) : false;

      if (eyesStillMoving || headStillMoving) {
        // Smooth transition back for eyes
        if (leftEyeRef?.current && rightEyeRef?.current) {
          leftEyeRef.current.rotation.x = THREE.MathUtils.lerp(
            leftEyeRef.current.rotation.x,
            initialLeftEyeRotation.x,
            0.1
          );
          leftEyeRef.current.rotation.y = THREE.MathUtils.lerp(
            leftEyeRef.current.rotation.y,
            initialLeftEyeRotation.y,
            0.1
          );

          rightEyeRef.current.rotation.x = THREE.MathUtils.lerp(
            rightEyeRef.current.rotation.x,
            initialRightEyeRotation.x,
            0.1
          );
          rightEyeRef.current.rotation.y = THREE.MathUtils.lerp(
            rightEyeRef.current.rotation.y,
            initialRightEyeRotation.y,
            0.1
          );
        }

        // Reset head if available
        if (headRef?.current && initialHeadRotation) {
          headRef.current.rotation.x = THREE.MathUtils.lerp(
            headRef.current.rotation.x || 0,
            initialHeadRotation.x,
            0.08
          );
          headRef.current.rotation.y = THREE.MathUtils.lerp(
            headRef.current.rotation.y || 0,
            initialHeadRotation.y,
            0.08
          );
          headRef.current.rotation.z = THREE.MathUtils.lerp(
            headRef.current.rotation.z || 0,
            initialHeadRotation.z,
            0.08
          );
        }

        resetAnimationFrameId = requestAnimationFrame(resetAnimation);
        if (resetAnimationFrameId) {
          animationController.trackRafId(resetAnimationFrameId);
        }
      } else {
        // Final exact position
        resetAnimationFrameId = null; // Clear the ID when done
        if (leftEyeRef?.current) {
          leftEyeRef.current.rotation.set(initialLeftEyeRotation.x, initialLeftEyeRotation.y, 0);
        }
        if (rightEyeRef?.current) {
          rightEyeRef.current.rotation.set(initialRightEyeRotation.x, initialRightEyeRotation.y, 0);
        }
        if (headRef?.current && initialHeadRotation) {
          headRef.current.rotation.x = initialHeadRotation.x;
          headRef.current.rotation.y = initialHeadRotation.y;
          headRef.current.rotation.z = initialHeadRotation.z;
        }
      }
    };

    resetAnimation();
  };

  const forceImmediateReset = () => {
    // IMMEDIATE synchronous reset (no lerp, no requestAnimationFrame)
    // Used when lip-sync is about to start to prevent interference
    isCancelled = true; // Set flag to prevent any new RAF callbacks
    cancelTrackedFrames();

    if (leftEyeRef?.current) {
      leftEyeRef.current.rotation.x = initialLeftEyeRotation.x;
      leftEyeRef.current.rotation.y = initialLeftEyeRotation.y;
      leftEyeRef.current.rotation.z = 0;
    }

    if (rightEyeRef?.current) {
      rightEyeRef.current.rotation.x = initialRightEyeRotation.x;
      rightEyeRef.current.rotation.y = initialRightEyeRotation.y;
      rightEyeRef.current.rotation.z = 0;
    }

    if (headRef?.current && initialHeadRotation) {
      headRef.current.rotation.x = initialHeadRotation.x;
      headRef.current.rotation.y = initialHeadRotation.y;
      headRef.current.rotation.z = initialHeadRotation.z;
    }

    console.log('Thinking animation FORCE reset (immediate, no lerp)');
  };

  const cancelAndHoldPose = () => {
    isCancelled = true;
    cancelTrackedFrames();
    console.log('Thinking animation cancelled while preserving current pose');
  };

  /**
   * Smooth transition to target positions (for blending with other animations)
   * @param targetPositions Target rotation positions
   * @param duration Transition duration in ms (default 300ms)
   */
  const transitionTo = (targetPositions: {
    headX?: number;
    headY?: number;
    headZ?: number;
    leftEyeX?: number;
    leftEyeY?: number;
    rightEyeX?: number;
    rightEyeY?: number;
  }, duration: number = 300) => {
    if (!leftEyeRef?.current || !rightEyeRef?.current) return;

    // Capture current positions as starting point
    const startPositions = {
      headX: headRef?.current?.rotation.x || 0,
      headY: headRef?.current?.rotation.y || 0,
      headZ: headRef?.current?.rotation.z || 0,
      leftEyeX: leftEyeRef.current.rotation.x,
      leftEyeY: leftEyeRef.current.rotation.y,
      rightEyeX: rightEyeRef.current.rotation.x,
      rightEyeY: rightEyeRef.current.rotation.y
    };

    const transitionStartTime = Date.now();

    const transitionAnimate = () => {
      const elapsed = Date.now() - transitionStartTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = progress < 0.5
        ? 2 * progress * progress
        : -1 + (4 - 2 * progress) * progress; // Ease in-out

      if (headRef?.current) {
        if (targetPositions.headX !== undefined) {
          headRef.current.rotation.x = THREE.MathUtils.lerp(startPositions.headX, targetPositions.headX, easedProgress);
        }
        if (targetPositions.headY !== undefined) {
          headRef.current.rotation.y = THREE.MathUtils.lerp(startPositions.headY, targetPositions.headY, easedProgress);
        }
        if (targetPositions.headZ !== undefined) {
          headRef.current.rotation.z = THREE.MathUtils.lerp(startPositions.headZ, targetPositions.headZ, easedProgress);
        }
      }

      if (leftEyeRef?.current) {
        if (targetPositions.leftEyeX !== undefined) {
          leftEyeRef.current.rotation.x = THREE.MathUtils.lerp(startPositions.leftEyeX, targetPositions.leftEyeX, easedProgress);
        }
        if (targetPositions.leftEyeY !== undefined) {
          leftEyeRef.current.rotation.y = THREE.MathUtils.lerp(startPositions.leftEyeY, targetPositions.leftEyeY, easedProgress);
        }
      }

      if (rightEyeRef?.current) {
        if (targetPositions.rightEyeX !== undefined) {
          rightEyeRef.current.rotation.x = THREE.MathUtils.lerp(startPositions.rightEyeX, targetPositions.rightEyeX, easedProgress);
        }
        if (targetPositions.rightEyeY !== undefined) {
          rightEyeRef.current.rotation.y = THREE.MathUtils.lerp(startPositions.rightEyeY, targetPositions.rightEyeY, easedProgress);
        }
      }

      if (progress < 1) {
        transitionAnimationFrameId = requestAnimationFrame(transitionAnimate);
        if (transitionAnimationFrameId) {
          animationController.trackRafId(transitionAnimationFrameId);
        }
      } else {
        if (transitionAnimationFrameId) {
          animationController.untrackRafId(transitionAnimationFrameId);
          transitionAnimationFrameId = null;
        }
      }
    };

    // Stop current animation during transition
    if (animationFrameId) {
      animationController.untrackRafId(animationFrameId);
      cancelAnimationFrame(animationFrameId);
      animationFrameId = null;
    }

    if (transitionAnimationFrameId) {
      animationController.untrackRafId(transitionAnimationFrameId);
      cancelAnimationFrame(transitionAnimationFrameId);
      transitionAnimationFrameId = null;
    }

    transitionAnimationFrameId = requestAnimationFrame(transitionAnimate);
    if (transitionAnimationFrameId) {
      animationController.trackRafId(transitionAnimationFrameId);
    }

    // Return cleanup function
    return () => {
      if (transitionAnimationFrameId) {
        animationController.untrackRafId(transitionAnimationFrameId);
        cancelAnimationFrame(transitionAnimationFrameId);
        transitionAnimationFrameId = null;
      }
    };
  };

  /**
   * Get current animation positions (for smooth transitions)
   */
  const getCurrentPositions = () => {
    return {
      headX: headRef?.current?.rotation.x || 0,
      headY: headRef?.current?.rotation.y || 0,
      headZ: headRef?.current?.rotation.z || 0,
      leftEyeX: leftEyeRef?.current?.rotation.x || 0,
      leftEyeY: leftEyeRef?.current?.rotation.y || 0,
      rightEyeX: rightEyeRef?.current?.rotation.x || 0,
      rightEyeY: rightEyeRef?.current?.rotation.y || 0
    };
  };

  return {
    start: () => {
      // Don't start if speaking is active
      if (animationController.isSpeaking()) {
        console.log('🤔 THINKING: Cannot start - speaking is active');
        return;
      }

      console.log('🤔 THINKING: Eyes look DOWN/AWAY, head tilts+turns (contemplative pose - NOT listening)');
      isCancelled = false; // Reset cancelled flag
      startTime = Date.now();
      animate();
    },
    stop: cleanup,
    cleanup,
    cancelAndHoldPose,
    forceImmediateReset,  // Export for external use (voice handlers)
    transitionTo,         // Smooth transition to target positions
    getCurrentPositions   // Get current positions for blending
  };
};


